import React, { useState, useEffect } from 'react';
import { collection, getDocs } from "firebase/firestore";
import { Link } from 'react-router-dom';
import Spinner from './Spinner.js';
import { BookOpen, CheckCircle, Clock, FileText, StickyNote, FileEdit, Package, Truck, Calendar, RefreshCw, ChevronDown } from 'lucide-react';

const StatCard = ({ title, value, icon, color, isCurrency = true }) => {
    const formatValue = (val) => {
        if (typeof val !== 'number') return val;
        if (!isCurrency) return val.toLocaleString('en-IN');
        return `₹${val.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
    };

    return (
        <div 
            className="stat-card-hover"
            style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                display: 'flex',
                alignItems: 'center',
                gap: '15px'
            }}
        >
            <div style={{
                padding: '12px',
                borderRadius: '50%',
                backgroundColor: color,
                color: 'white'
            }}>
                {icon}
            </div>
            <div>
                <h3 style={{ margin: 0, fontSize: '14px', color: '#6b7280', textTransform: 'uppercase' }}>{title}</h3>
                <p style={{ margin: '5px 0 0', fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>{formatValue(value)}</p>
            </div>
        </div>
    );
};

const Dashboard = ({ db, user }) => {
    const [stats, setStats] = useState(null);
    const [recentActivity, setRecentActivity] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [timePeriod, setTimePeriod] = useState('this_month'); // 'today', 'this_week', 'this_month'
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isMobile = windowWidth <= 768;

    const fetchData = async (showRefreshSpinner = false) => {
        if (showRefreshSpinner) setIsRefreshing(true);
        else setLoading(true);

        try {
            // Fetch all data in parallel
            const fetchPromises = [getDocs(collection(db, "orders"))];
            
            // Only fetch quotes and invoices if not ops
            if (user?.role !== 'ops') {
                fetchPromises.push(getDocs(collection(db, "quotes")));
                fetchPromises.push(getDocs(collection(db, "invoices")));
            }

            const results = await Promise.all(fetchPromises);
            
            const orders = results[0].docs.map(doc => ({ ...doc.data(), id: doc.id, type: 'Order' }));
            let quotes = [];
            let invoices = [];

            if (user?.role !== 'ops') {
                quotes = results[1].docs.map(doc => ({ ...doc.data(), id: doc.id, type: 'Quote' }));
                invoices = results[2].docs.map(doc => ({ ...doc.data(), id: doc.id, type: 'Invoice' }));
            }

            // --- Define Time Filter ---
            const now = new Date();
            const filterDate = new Date();
            filterDate.setHours(0, 0, 0, 0);

            if (timePeriod === 'today') {
                // filterDate is already today 00:00
            } else if (timePeriod === 'this_week') {
                const day = filterDate.getDay();
                const diff = filterDate.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
                filterDate.setDate(diff);
            } else if (timePeriod === 'this_month') {
                filterDate.setDate(1);
            }
            
            const filterFn = (item) => {
                const itemDate = new Date(item.date || item.createdAt);
                return itemDate >= filterDate;
            };

            // --- Calculate Stats ---
            const filteredQuotes = quotes.filter(filterFn);
            const filteredInvoices = invoices.filter(filterFn);
            const filteredOrders = orders.filter(filterFn);

            const openQuotesValue = filteredQuotes
                .filter(q => q.status === 'sent')
                .reduce((sum, q) => sum + (q.grandTotal || 0), 0);

            const outstandingRevenue = filteredInvoices
                .filter(i => i.status === 'sent' && !i.paid)
                .reduce((sum, i) => sum + (i.grandTotal || 0), 0);
            
            const paidValue = filteredInvoices
                .filter(i => i.status === 'paid')
                .reduce((sum, i) => sum + (i.grandTotal || 0), 0);

            const confirmedOrdersValue = filteredOrders
                .filter(o => o.status === 'confirmed_order')
                .reduce((sum, o) => sum + (o.orderValue || 0), 0);

            // --- Calculate Ops Metrics (Counts) ---
            const confirmedOrdersCount = filteredOrders.filter(o => o.status === 'confirmed_order').length;
            const packingOrdersCount = filteredOrders.filter(o => o.status === 'packing').length;
            const shippedOrdersCount = filteredOrders.filter(o => o.status === 'shipped' || o.status === 'partially_shipped').length;
            const deliveredOrdersCount = filteredOrders.filter(o => o.status === 'delivered').length;

            setStats({ 
                openQuotesValue, 
                outstandingRevenue, 
                paidLast30Days: paidValue, // Renamed conceptually to filtered period paid
                confirmedOrdersValue,
                confirmedOrdersCount,
                packingOrdersCount,
                shippedOrdersCount,
                deliveredOrdersCount
            });
            
            // --- Aggregate Recent Activity ---
            const allActivity = [...orders];
            if (user?.role !== 'ops') {
                allActivity.push(...quotes, ...invoices);
            }
            
            allActivity.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setRecentActivity(allActivity.slice(0, 10));

        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [db, user?.role, timePeriod]);

    if (loading || !stats) {
        return <Spinner />;
    }

    const getActivityIcon = (type) => {
        switch(type) {
            case 'Quote': return <StickyNote size={18} />;
            case 'Invoice': return <FileText size={18} />;
            case 'Order': return <BookOpen size={18} />;
            default: return <FileEdit size={18} />;
        }
    };
    
    const getActivityPath = (item) => {
        switch(item.type) {
            case 'Quote': return `/quotes/${item.id}`;
            case 'Invoice': return `/invoices/${item.id}`;
            case 'Order': return `/orders/${item.id}`;
            default: return '/';
        }
    }

    const getStatusStyles = (status) => {
        switch (status?.toLowerCase()) {
            case 'draft': return { bg: '#f3f4f6', text: '#374151' };
            case 'sent': return { bg: '#eff6ff', text: '#2563eb' };
            case 'confirmed_order': 
            case 'confirmed': return { bg: '#dcfce7', text: '#166534' };
            case 'paid': return { bg: '#dcfce7', text: '#166534' };
            case 'partially_shipped': return { bg: '#e0e7ff', text: '#4338ca' };
            case 'invoiced': return { bg: '#fef9c3', text: '#854d0e' };
            case 'cancelled': return { bg: '#fee2e2', text: '#991b1b' };
            default: return { bg: '#f3f4f6', text: '#374151' };
        }
    };

    const headerContainerStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px'
    };

    const actionGroupStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    };

    const selectStyle = {
        padding: '8px 12px',
        borderRadius: '6px',
        border: '1px solid #e5e7eb',
        backgroundColor: 'white',
        fontSize: '14px',
        color: '#374151',
        cursor: 'pointer',
        outline: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '5px'
    };

    const refreshButtonStyle = {
        padding: '8px',
        borderRadius: '6px',
        border: '1px solid #e5e7eb',
        backgroundColor: 'white',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
        color: '#374151'
    };

    return (
        <div style={{ padding: isMobile ? '10px' : '0' }}>
            <div style={headerContainerStyle}>
                <h2 style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>Dashboard</h2>
                
                <div style={actionGroupStyle}>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <div style={{ position: 'absolute', left: '10px', pointerEvents: 'none' }}>
                            <Calendar size={16} color="#6b7280" />
                        </div>
                        <select 
                            value={timePeriod} 
                            onChange={(e) => setTimePeriod(e.target.value)}
                            style={{ ...selectStyle, paddingLeft: '32px' }}
                        >
                            <option value="today">Today</option>
                            <option value="this_week">This Week</option>
                            <option value="this_month">This Month</option>
                        </select>
                    </div>

                    <button 
                        onClick={() => fetchData(true)} 
                        disabled={isRefreshing}
                        style={{
                            ...refreshButtonStyle,
                            opacity: isRefreshing ? 0.5 : 1
                        }}
                        title="Refresh Data"
                    >
                        <RefreshCw size={18} className={isRefreshing ? "animate-spin" : ""} />
                    </button>
                </div>
            </div>

            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))', 
                gap: '15px', 
                marginBottom: '30px' 
            }}>
                {user?.role !== 'ops' ? (
                    <>
                        <StatCard title="Open Pipeline" value={stats.openQuotesValue} icon={<StickyNote size={24} />} color="#3b82f6" />
                        <StatCard title="Outstanding Revenue" value={stats.outstandingRevenue} icon={<Clock size={24} />} color="#ef4444" />
                        <StatCard title="Paid (Selected Period)" value={stats.paidLast30Days} icon={<CheckCircle size={24} />} color="#22c55e" />
                        <StatCard title="Confirmed Orders" value={stats.confirmedOrdersValue} icon={<BookOpen size={24} />} color="#f59e0b" />
                    </>
                ) : (
                    <>
                        <StatCard title="Confirmed Orders" value={stats.confirmedOrdersCount} icon={<BookOpen size={24} />} color="#f59e0b" isCurrency={false} />
                        <StatCard title="Packing" value={stats.packingOrdersCount} icon={<Package size={24} />} color="#8b5cf6" isCurrency={false} />
                        <StatCard title="Shipped" value={stats.shippedOrdersCount} icon={<Truck size={24} />} color="#3b82f6" isCurrency={false} />
                        <StatCard title="Delivered" value={stats.deliveredOrdersCount} icon={<CheckCircle size={24} />} color="#10b981" isCurrency={false} />
                    </>
                )}
            </div>

            <div>
                <h3 style={{ marginBottom: '15px', fontSize: isMobile ? '18px' : '20px' }}>
                    Recent Activity
                </h3>
                <div style={{ 
                    backgroundColor: 'white', 
                    borderRadius: '8px', 
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    overflow: 'hidden'
                }}>
                    {recentActivity.length > 0 ? (
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                            {recentActivity.map((item, index) => {
                                const statusStyle = getStatusStyles(item.status);
                                return (
                                <li key={item.id} className="hover-highlight" style={{ borderBottom: index !== recentActivity.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                                    <Link to={getActivityPath(item)} style={{ 
                                        textDecoration: 'none', 
                                        color: 'inherit', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: isMobile ? '10px' : '15px', 
                                        padding: isMobile ? '12px' : '15px' 
                                    }}>
                                        <div style={{ color: '#6b7280', display: 'flex', alignItems: 'center' }}>
                                            {getActivityIcon(item.type)}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <p style={{ 
                                                    margin: 0, 
                                                    fontWeight: '600', 
                                                    fontSize: isMobile ? '14px' : '16px',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {item.type}: {item.type === 'Order' ? (item.orderNumber || item.id) : (item.quoteNumber || item.voucherNo || 'N/A')}
                                                </p>
                                                <span style={{
                                                    fontSize: '10px',
                                                    padding: '2px 8px',
                                                    borderRadius: '12px',
                                                    fontWeight: 'bold',
                                                    textTransform: 'uppercase',
                                                    backgroundColor: statusStyle.bg,
                                                    color: statusStyle.text,
                                                    flexShrink: 0
                                                }}>
                                                    {item.status?.replace('_', ' ') || 'Unknown'}
                                                </span>
                                            </div>
                                            <p style={{ 
                                                margin: '4px 0 0', 
                                                fontSize: isMobile ? '12px' : '13px', 
                                                color: '#6b7280',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>
                                                {item.customerName} - {new Date(item.createdAt).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <div style={{ 
                                            fontWeight: 'bold', 
                                            fontSize: isMobile ? '13px' : '14px',
                                            textAlign: 'right',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            ₹{(item.grandTotal || item.orderValue || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                        </div>
                                    </Link>
                                </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <p style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No activity to display.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
